import { createApi } from '@reduxjs/toolkit/query/react';
import { type IntakeStatus } from '@strata/react-status-indicators';
import { type IntakeCaseMilestone } from '@strata/react-timeline';

import { INTAKE_CASE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface IntakeCaseTreatmentSummary {
    prescriber_first_name: string;
    prescriber_last_name: string;
    create_date: string;
    submit_date: string;
    last_updated: string;
    intake_status: IntakeStatus;
    status_detail: string;
}

export interface IntakeCasesByMemberDrugResponse {
    cases: IntakeCaseTreatmentSummary[];
    milestones?: IntakeCaseMilestone[];
}

export interface GetIntakeCaseMilestonesProps {
    segmentId: string;
    memberId: string;
    memberFirstName: string;
    memberDOB: string;
    productServiceOid?: string;
    /** ISO 8601 UTC datetime (`key_date` query param). */
    keyDate: string;
    /** ISO 8601 UTC datetime (`end_date` query param). */
    endDate?: string;
}

type IntakeCaseMilestoneApiRow = Partial<IntakeCaseMilestone> & { milestone_date?: string };

export const normalizeIntakeMilestonesResponse = (
    response: IntakeCaseMilestoneApiRow[] | undefined,
): IntakeCaseMilestone[] =>
    (response ?? []).map((milestone) => ({
        ...milestone,
        timestamp: milestone.timestamp ?? milestone.milestone_date,
    })) as IntakeCaseMilestone[];

export const intakeCaseApi = createApi({
    baseQuery: getBaseQuery(INTAKE_CASE_API_URL),
    reducerPath: 'intakeCaseApi',
    endpoints: (build) => ({
        getIntakeCaseVisibilty: build.query<string[], void>({
            query: () => ({
                url: `client/payer_segment_config/visibility`,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred loading intake case configuration.',
            ),
        }),
        getIntakeCasesByMemberDrug: build.query<
            IntakeCaseTreatmentSummary[],
            {
                segmentId: string;
                memberId: string;
                memberFirstName: string;
                memberDOB: string;
                productServiceOid?: string;
            }
        >({
            query: ({ segmentId, memberId, memberFirstName, memberDOB, productServiceOid }) => ({
                url: `client/intake_cases/by_member_drug/${segmentId}`,
                params: {
                    member_id: memberId,
                    member_first_name: memberFirstName,
                    member_dob: memberDOB,
                    product_service_oid: productServiceOid,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving intake cases for the treatment.',
            ),
        }),
        getMilestonesByMemberDrug: build.query<IntakeCaseMilestone[], GetIntakeCaseMilestonesProps>(
            {
                query: ({
                    segmentId,
                    memberId,
                    memberFirstName,
                    memberDOB,
                    productServiceOid,
                    keyDate,
                    endDate,
                }) => ({
                    url: `client/intake_cases/milestones/by_member_drug/${segmentId}`,
                    params: {
                        member_id: memberId,
                        member_first_name: memberFirstName,
                        member_dob: memberDOB,
                        product_service_oid: productServiceOid,
                        key_date: keyDate,
                        end_date: endDate,
                    },
                }),
                transformResponse: normalizeIntakeMilestonesResponse,
                transformErrorResponse: handleRTKQueryError(
                    'An error occurred retrieving intake cases for the treatment.',
                ),
            },
        ),
    }),
});

export const {
    useGetIntakeCaseVisibiltyQuery,
    useGetIntakeCasesByMemberDrugQuery,
    useGetMilestonesByMemberDrugQuery,
} = intakeCaseApi;
